import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { isProductionWeb } from '@/hotdesk-ui/helpers/reviewable-booking';

/**
 * Surfaces that can show rating badges / review lists.
 * Maps to Firebase JSON keys `web_reviews_{screen}`.
 */
export type WebReviewsDisplayScreen =
    | 'workspace_explore'
    | 'workspace_home'
    | 'workspace_details'
    | 'workspace_booking_details'
    | 'workspace_booking_review'
    | 'listing_explore'
    | 'listing_home'
    | 'listing_details'
    | 'listing_workspace'
    | 'listing_booking_details'
    | 'listing_booking_review';

const DEFAULT_PAGINATION_LIMIT = 1;

function remoteFlagKey(screen: WebReviewsDisplayScreen): string {
    return `web_reviews_${screen}`;
}

function isWorkspaceScreen(screen: WebReviewsDisplayScreen): boolean {
    return screen.startsWith('workspace_');
}

/**
 * Whether ratings/reviews may render on a given screen.
 * Local + QA: on (so existing UI keeps working while flags are tuned).
 * Prod: master switch + per-screen JSON flag. If JSON is missing, fall back
 * to legacy `app_reviews_feature_switch` so prod isn't blanked unexpectedly.
 */
export async function loadWebReviewsDisplayEnabled(
    screen: WebReviewsDisplayScreen
): Promise<boolean> {
    if (!isProductionWeb()) return true;

    try {
        const featureOn = await fetchRemoteConfig(
            RemoteConfigKeys.WEB_REVIEWS_FEATURE_SWITCH,
            'boolean'
        );
        if (featureOn !== true) return false;

        const jsonKey = isWorkspaceScreen(screen)
            ? RemoteConfigKeys.WEB_REVIEWS_WORKSPACE_SCREENS_SWITCH
            : RemoteConfigKeys.WEB_REVIEWS_LISTING_SCREENS_SWITCH;

        const config = (await fetchRemoteConfig(jsonKey, 'object')) as
            | Record<string, unknown>
            | undefined;

        if (!config || typeof config !== 'object' || Array.isArray(config)) {
            const legacy = await fetchRemoteConfig(
                RemoteConfigKeys.APP_REVIEWS_FEARURE_SWITCH,
                'boolean'
            );
            return legacy === true;
        }

        return config[remoteFlagKey(screen)] === true;
    } catch {
        return false;
    }
}

/** Initial visible review cards; PRD default 1. */
export async function loadWebReviewsPaginationLimit(): Promise<number> {
    try {
        const limit = await fetchRemoteConfig(
            RemoteConfigKeys.WEB_REVIEWS_PAGINATION_LIMIT,
            'number'
        );
        if (typeof limit === 'number' && limit > 0) return Math.floor(limit);
    } catch {
        /* fall through */
    }
    return DEFAULT_PAGINATION_LIMIT;
}
